import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';

import { getAsset } from '@/client/utils/assets';
import {
  deriveEntityStatus,
  entityStatusBadgeClass,
} from '@/client/utils/filters';
import { Skeleton } from '@/components/ui/skeleton';

/**
 * Listings table matching the ListManagement listings page, reused by the user
 * Listings tab and the org listings section. Admin listing rows come in two
 * shapes — nested `{ Entity, Assets, EntityType, Prices }` (the entities list)
 * and flat `{ eid, name, slug, active, published, date_added }` (the per-user
 * endpoint) — so every field access is normalized and guarded.
 */

const eidOf = (row: any): number | undefined => row?.Entity?.eid ?? row?.eid;
const nameOf = (row: any): string => {
  const e = row?.Entity ?? row;
  return (
    e?.name_basic ||
    e?.name_marketing ||
    e?.name ||
    row?.name ||
    `Listing #${eidOf(row) ?? '—'}`
  );
};
const coverOf = (row: any): string | undefined => {
  // A ready-made URL (backend `cover`/`cover_url`) wins; otherwise build from an
  // asset object. getAsset(undefined) returns the placeholder, so this is always
  // safe. NOTE: the per-user listings endpoint returns none of these today, so
  // those rows show the placeholder until the backend includes a cover.
  const direct = row?.cover_url ?? (typeof row?.cover === 'string' ? row.cover : undefined);
  if (direct) return direct;
  const asset =
    row?.Assets?.[0] ??
    row?.Entity?.Assets?.[0] ??
    (row?.cover && typeof row.cover === 'object' ? row.cover : undefined);
  return getAsset(asset);
};
const typeOf = (row: any): string => {
  const t = row?.EntityType?.name_friendly || row?.EntityType?.name_system;
  if (t) return t;
  // Fall back to the slug's category path (e.g. "homes/apartment/…").
  const slug = row?.Entity?.slug ?? row?.slug;
  if (typeof slug === 'string' && slug.includes('/')) {
    return slug
      .split('/')
      .slice(0, 2)
      .map((s) => s.replace(/_/g, ' '))
      .join(' · ');
  }
  return '—';
};
const priceOf = (row: any): string => {
  const p = row?.Prices?.[0] ?? row?.Entity?.Prices?.[0];
  if (!p || p.price == null) return '';
  return `${p.ccode || 'GHS'} ${Number(p.price).toLocaleString()}`;
};
const addedOf = (row: any): string | null =>
  (row?.Entity ?? row)?.date_added ?? row?.date_added ?? null;

interface EntityTableProps {
  entities: any[];
  isLoading?: boolean;
  isError?: boolean;
  emptyMessage?: string;
  errorMessage?: string;
}

const COLS = 6;

export default function EntityTable({
  entities,
  isLoading,
  isError,
  emptyMessage = 'No listings found.',
  errorMessage = 'Could not load listings.',
}: EntityTableProps) {
  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {['Entity', 'Type', 'Price', 'Status', 'Added', ''].map((h, i) => (
                <th
                  key={h || i}
                  className={`px-6 py-3 text-xs font-medium uppercase tracking-wider text-gray-500 ${
                    h === '' ? 'text-right' : 'text-left'
                  }`}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {isLoading &&
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i}>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <Skeleton className="h-12 w-16 rounded" />
                      <div className="ml-4 space-y-2">
                        <Skeleton className="h-4 w-40" />
                        <Skeleton className="h-3 w-24" />
                      </div>
                    </div>
                  </td>
                  {Array.from({ length: 5 }).map((__, j) => (
                    <td key={j} className="px-6 py-4">
                      <Skeleton className="h-4 w-16" />
                    </td>
                  ))}
                </tr>
              ))}

            {!isLoading && isError && (
              <tr>
                <td
                  colSpan={COLS}
                  className="px-6 py-12 text-center text-sm text-rose-600"
                >
                  {errorMessage}
                </td>
              </tr>
            )}

            {!isLoading && !isError && entities.length === 0 && (
              <tr>
                <td
                  colSpan={COLS}
                  className="px-6 py-12 text-center text-sm text-gray-400"
                >
                  {emptyMessage}
                </td>
              </tr>
            )}

            {!isLoading &&
              !isError &&
              entities.map((row) => {
                const eid = eidOf(row);
                const status = deriveEntityStatus(row);
                const price = priceOf(row);
                const added = addedOf(row);
                return (
                  <tr key={eid ?? nameOf(row)} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="h-12 w-16 flex-shrink-0 overflow-hidden rounded bg-gray-100">
                          <img
                            className="h-12 w-16 object-cover"
                            src={coverOf(row)}
                            alt=""
                          />
                        </div>
                        <div className="ml-4 min-w-0">
                          <div className="truncate text-sm font-medium text-gray-900">
                            {nameOf(row)}
                          </div>
                          <div className="text-xs text-gray-500">
                            #{eid ?? '—'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700">
                        {typeOf(row)}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                      {price || <span className="text-gray-400">N/A</span>}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${entityStatusBadgeClass(
                          status
                        )}`}
                      >
                        {status.charAt(0) + status.slice(1).toLowerCase()}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-xs text-gray-500">
                      {added ? new Date(added).toLocaleDateString() : '—'}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-right">
                      {eid != null && (
                        <Link
                          to={`/admin/listings/${eid}`}
                          className="inline-flex items-center gap-1 text-sm font-medium text-indigo-600 hover:underline"
                        >
                          View
                          <ExternalLink className="h-3.5 w-3.5" />
                        </Link>
                      )}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
